import type { MondayClient } from "./monday-client";

/** How many pages any sweep or lookup walks before giving up and saying so. */
const MAX_PAGES = 20;

type Page<T> = { cursor?: string | null; items?: T[] };

/**
 * Walk a board's `items_page` to exhaustion, or say loudly that we could not.
 * `rules` is appended to the first page's arguments (e.g. `, query_params:{…}`);
 * `selection` is the item fields to read.
 */
export async function pagedItems<T>(
  client: MondayClient,
  boardId: string,
  rules: string,
  selection: string,
  requireCompleteCursor = false
): Promise<T[]> {
  const out: T[] = [];
  let cursor: string | null = null;
  const seen = new Set<string>();

  for (let page = 0; page < MAX_PAGES; page++) {
    const data = (await client.gql(
      cursor
        ? `query($c:String!){ next_items_page(limit:100, cursor:$c){ cursor items { ${selection} } } }`
        : `query($b:[ID!]){ boards(ids:$b){ items_page(limit:100${rules}){ cursor items { ${selection} } } } }`,
      cursor ? { c: cursor } : { b: [boardId] }
    )) as { boards?: { items_page?: Page<T> }[]; next_items_page?: Page<T> } | null;

    const pageData: Page<T> | undefined = cursor
      ? data?.next_items_page
      : data?.boards?.[0]?.items_page;
    if (!pageData || !Array.isArray(pageData.items)) {
      throw new Error(`could not read a complete items page from board ${boardId}`);
    }
    out.push(...pageData.items);
    if (
      requireCompleteCursor &&
      !(pageData.cursor === null || (typeof pageData.cursor === "string" && pageData.cursor))
    )
      throw new Error(`board ${boardId} returned an incomplete cursor`);
    cursor = pageData.cursor ?? null;
    if (!cursor) return out;
    if (requireCompleteCursor && seen.has(cursor))
      throw new Error(`board ${boardId} repeated a cursor`);
    seen.add(cursor);
  }

  // NOT a silent truncation. A partial answer reads as "work not found" or "no
  // such account", both of which the callers would report as fact.
  throw new Error(
    `board ${boardId} still had pages after ${MAX_PAGES} — refusing to treat a partial result as complete`
  );
}
